import useInterval from '@restart/hooks/useInterval';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useMemo, useRef, useState } from 'react';
import { MdOutlineArrowBack } from 'react-icons/md';
import { useSelector } from 'react-redux';

import styles from './index.module.scss';
import Window from './Window';

import EmptyList from '@/components/ui/EmptyList';

const cx = classNames.bind(styles);

const Live = () => {
  const robots = useSelector((state) => state.robots);
  const targetRobotIds = useSelector((state) => state.robot.targetRobotIds);
  const [orderedRobots, setOrderedRobots] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [isIntervalPaused, setIntervalPaused] = useState(true);
  const pendingChannelRobots = useRef(new Set());
  const windowRefs = useRef(new Map());

  useUpdateEffect(() => {
    const targetSet = new Set(targetRobotIds);
    const orderedSet = new Set(orderedRobots.map(({ id }) => id));

    const kept = orderedRobots.filter(({ id }) => targetSet.has(id));
    const added = targetRobotIds
      .filter((id) => !orderedSet.has(id))
      .map((id) => robots.find((robot) => robot.id === id));

    setOrderedRobots([...kept, ...added]);
  }, [targetRobotIds, robots]);

  useInterval(
    () => {
      pendingChannelRobots.current.forEach((robotId) => {
        const windowRef = windowRefs.current.get(robotId);
        if (windowRef) {
          windowRef.load();
        } else {
          // ref가 존재하지 않으면 제거
          pendingChannelRobots.current.delete(robotId);
        }
      });
    },
    5000,
    isIntervalPaused
  );

  const layout = useMemo(() => {
    const cols = Math.ceil(Math.sqrt(orderedRobots.length));
    const rows = Math.ceil(orderedRobots.length / cols);

    return { cols, rows };
  }, [orderedRobots.length]);

  const displayedRobots = useMemo(() => {
    if (draggedIndex === null || hoverIndex === null) {
      return orderedRobots;
    }

    const reordered = [...orderedRobots];
    const draggedItem = reordered[draggedIndex];
    reordered.splice(draggedIndex, 1);
    reordered.splice(hoverIndex, 0, draggedItem);

    return reordered;
  }, [orderedRobots, draggedIndex, hoverIndex]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setHoverIndex(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex !== hoverIndex && hoverIndex !== null) {
      const nextOrderedRobots = [...orderedRobots];
      const draggedItem = nextOrderedRobots[draggedIndex];

      nextOrderedRobots.splice(draggedIndex, 1);
      nextOrderedRobots.splice(hoverIndex, 0, draggedItem);
      setOrderedRobots(nextOrderedRobots);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setHoverIndex(null);
  };

  const setWindowRef = (robotId, ref) => {
    if (ref) {
      windowRefs.current.set(robotId, ref);
    } else {
      windowRefs.current.delete(robotId);
    }
  };

  const handleChannelResult = (robotId, hasChannels) => {
    if (hasChannels) {
      pendingChannelRobots.current.delete(robotId);
    } else {
      pendingChannelRobots.current.add(robotId);
    }

    setIntervalPaused(pendingChannelRobots.current.size === 0);
  };

  return (
    <div className={cx('container')}>
      {orderedRobots.length === 0 && (
        <div className={cx('empty')}>
          <EmptyList className={cx('message')} />
          <div className={cx('pleaseWrapper')}>
            <div className={cx('arrow')}>
              <MdOutlineArrowBack size={16} color="gray" />
            </div>
            <div className={cx('please')}>Please select a robot on the left.</div>
          </div>
        </div>
      )}
      {orderedRobots.length > 0 && (
        <div className={cx('grid')} style={{ '--cols': layout.cols, '--rows': layout.rows }}>
          {displayedRobots.map((robot, index) => {
            const originalIndex = orderedRobots.indexOf(robot);
            const isDragging = originalIndex === draggedIndex;

            return (
              <div
                key={robot.id}
                draggable
                className={cx('cell', { dragging: isDragging })}
                onDragStart={() => handleDragStart(originalIndex)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}>
                <Window
                  ref={(ref) => setWindowRef(robot.id, ref)}
                  robot={robot}
                  onChannelResult={handleChannelResult}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Live;
